import { STRIPE_API_VERSION } from "./stripe";
/**
 * Read-only Stripe smoke tests behind the builder debug token. Every check is a GET; nothing is created or changed, and
 * no key, secret or customer data is ever returned, only ids, booleans and HTTP statuses.
 */
const CONNECT_EVENTS = [
    "checkout.session.completed", "checkout.session.async_payment_succeeded", "checkout.session.async_payment_failed",
    "checkout.session.expired", "charge.refunded", "refund.updated",
];
const ACCOUNT_ID = /^acct_[A-Za-z0-9]{8,64}$/;
const MIN_TOKEN_LENGTH = 32;
const encoder = new TextEncoder();
/** Compares SHA-256 digests so the comparison takes the same time whatever the token length; short tokens never match. */
export async function stripeDebugTokenMatches(expected, authorization) {
    if (!expected || expected.length < MIN_TOKEN_LENGTH)
        return false;
    const supplied = authorization?.startsWith("Bearer ") ? authorization.slice("Bearer ".length) : "";
    const [a, b] = await Promise.all([expected, supplied].map((value) => crypto.subtle.digest("SHA-256", encoder.encode(value))));
    const left = new Uint8Array(a), right = new Uint8Array(b);
    let difference = supplied.length === 0 ? 1 : 0;
    for (let index = 0; index < left.length; index += 1)
        difference |= left[index] ^ right[index];
    return difference === 0;
}
/** What kind of key it is, from its prefix alone. */
export function stripeKeyKind(key) {
    if (!key)
        return { set: false, mode: "unknown", restricted: false };
    return { set: true, mode: /_test_/.test(key) ? "test" : /_live_/.test(key) ? "live" : "unknown", restricted: key.startsWith("rk_") };
}
async function get(fetcher, key, path, account) {
    const headers = { Authorization: `Bearer ${key}`, "Stripe-Version": STRIPE_API_VERSION };
    if (account)
        headers["Stripe-Account"] = account;
    try {
        const response = await fetcher(`https://api.stripe.com/v1/${path}`, { headers, signal: AbortSignal.timeout(10_000) });
        return { status: response.status, body: await response.json().catch(() => null) };
    }
    catch {
        return { status: 0, body: null };
    }
}
/** Admin: the platform key, Connect access, and the shop's Connect webhook. */
export async function platformStripeReport(input) {
    const fetcher = input.fetcher ?? fetch;
    const key = stripeKeyKind(input.key);
    const report = { apiVersion: STRIPE_API_VERSION, key, webhookSecretSet: input.webhookSecretSet };
    if (!input.key)
        return { ...report, ok: false, problems: ["STRIPE_SECRET_KEY is not set"] };
    const problems = [];
    const account = await get(fetcher, input.key, "account");
    const settings = account.body?.settings?.dashboard;
    report.account = account.status === 200 ? { id: account.body?.id, name: settings?.display_name ?? null, country: account.body?.country } : { status: account.status };
    if (account.status !== 200)
        problems.push(`Reading the account failed (${account.status})`);
    const connect = await get(fetcher, input.key, "accounts?limit=1");
    report.connect = connect.status === 200 ? "on" : { status: connect.status };
    if (connect.status !== 200)
        problems.push("Connect isn't available to this key");
    const hooks = await get(fetcher, input.key, "webhook_endpoints?limit=100");
    const matching = (hooks.body?.data ?? []).filter((hook) => hook.url === input.webhookUrl);
    const missing = matching[0] ? CONNECT_EVENTS.filter((event) => !matching[0].enabled_events.includes(event)) : [...CONNECT_EVENTS];
    report.webhook = { url: input.webhookUrl, found: matching.map((hook) => ({ id: hook.id, status: hook.status })), missingEvents: missing };
    if (!matching.length)
        problems.push("No webhook for the shop's Connect address");
    if (matching.length > 1)
        problems.push("More than one webhook for the shop's Connect address");
    if (matching[0] && matching[0].status !== "enabled")
        problems.push("The shop webhook isn't enabled");
    if (missing.length)
        problems.push(`The shop webhook is missing: ${missing.join(", ")}`);
    if (!input.webhookSecretSet)
        problems.push("STRIPE_CONNECT_WEBHOOK_SECRET is not set");
    return { ...report, ok: problems.length === 0, problems };
}
/**
 * Public site: the restricted shop key can read what checkout needs and is refused what it shouldn't have. With an
 * `account`, the same reads run on that connected account (the permissions a direct charge uses). Write permissions
 * can't be checked without side effects; a real checkout start proves them.
 */
export async function shopKeyStripeReport(input) {
    const fetcher = input.fetcher ?? fetch;
    const key = stripeKeyKind(input.key);
    const report = { apiVersion: STRIPE_API_VERSION, key, downloadSigningSecretSet: input.signingSecretSet };
    const problems = [];
    if (!input.signingSecretSet)
        problems.push("SHOP_DOWNLOAD_SIGNING_SECRET is not set (32+ characters)");
    if (!input.key)
        return { ...report, ok: false, problems: ["STRIPE_SHOP_KEY is not set", ...problems] };
    if (!key.restricted)
        problems.push("STRIPE_SHOP_KEY should be a restricted key (rk_...)");
    const account = input.account && ACCOUNT_ID.test(input.account) ? input.account : undefined;
    if (input.account && !account)
        problems.push("The account parameter isn't a Stripe account id");
    const scopes = [["platform", undefined], ...(account ? [[account, account]] : [])];
    const checks = {};
    for (const [label, scope] of scopes) {
        const needed = { checkoutSessions: "checkout/sessions?limit=1", refunds: "refunds?limit=1", paymentIntents: "payment_intents?limit=1" };
        const refused = { charges: "charges?limit=1", balanceTransactions: "balance_transactions?limit=1" };
        const result = {};
        for (const [name, path] of Object.entries(needed)) {
            result[name] = (await get(fetcher, input.key, path, scope)).status;
            if (result[name] !== 200)
                problems.push(`${label}: can't read ${name} (${result[name]})`);
        }
        for (const [name, path] of Object.entries(refused)) {
            result[name] = (await get(fetcher, input.key, path, scope)).status;
            if (result[name] === 200)
                problems.push(`${label}: the key can read ${name}, which it shouldn't`);
        }
        checks[label] = result;
    }
    return { ...report, checks, ok: problems.length === 0, problems, note: account ? undefined : "Add ?account=acct_... to check a connected account's permissions too." };
}
